const _ = require("lodash");
const Constants = require("../../../shared/Constants");
const {setIn, extendIn} = require("../../../shared/ImmHelpers");
const StoreUtil = require("../../../shared/StoreUtil");
const {
	checkPassword,
	checkEmailPassword,
} = require("../../../shared/PasswordCheck");
const LoadState = require("../../core/vault/util/LoadState");
const Cursor = require("../../core/vault/util/Cursor");
const ErrorUtil = require("../../../shared/ErrorUtil");
const OpsEditSave = require("../../core/vault/OpsEditSave");
const OpsCheckoutForm = require("../../office/vault/OpsCheckoutForm");
const OpsParentPayments = require("./OpsParentPayments");
const moment = require("moment");

const {
	MAX_SUBSCRIPTIONS_FOR_PARENT,
	MAX_INACTIVE_SUBSCRIPTIONS_FOR_PARENT,
} = Constants;

/*
ParentAcc shape {
  load,
  loadError,
  isOwner: boolean, // if not an owner, this is false and nothing is loaded
  shownCC: null | {
    cardType,
    cc4,
    expMonth,
    expYear,
  },
  hasShipping: boolean,
  storedForm: OpsCheckoutForm object,
  platformStudents: Array of all BA students attached to this platform user (includes academy students),
  students: Array of Student objects,
  payments: OpsParentPayments object,
  subChangeState: null or OpsParentSubChange object,
  lastFinishedSCS: a portion of subChangeState that finished an order; see
    OpsParentSubChange getFinishObject function for shape,
  isCancelPending,
  hasPassword: boolean,
  es: OpsEditSave helper,
}
PlatformStudent shape {
{
	id: 'e4e308a8-0c4e-47ac-8c8c-59b0cb51f4a2',
	email: null,
	username: 'VCStudentClassEnded',
	firstName: 'VCStudentClassEnded',
	lastName: 'Bauer',
	displayName: 'VCStudentClassEnded',
	isRestricted: false,
	isNationalStaff: false,
	isTestAccount: false,
	emailVerifiedAt: null,
	lastLoginAt: null,
	lastActiveAt: null,
	deactivatedAt: null,
	createdAt: '2024-12-10T20:03:25.516Z',
	updatedAt: '2024-12-10T20:04:57.433Z',
	bypassesCoppa: false,
	shouldMfa: false,
	userID: 102400003
}
Student shape {
  userID,
  avatar,
  username,
  displayName,
  firstName,
  lastName,
  hasCode,
  usedCode,
	grade,
	customerSegment,
  lockSettings,
  subscription: Subscription object,
}
Subscription shape {
  subscriptionID,
  active,
  planType,
  price,
  expiresAt,
  expiredAt,
  weeklyNotification,
  autoRenew,
  refundable,
  failedAttempts,
}
*/

const init = function(now) {
	return LoadState.loadInit({
		shownCC: null,
		hasShipping: false,
		storedForm: OpsCheckoutForm.init("parent", now),
		platformStudents: [],
		students: [],
		payments: OpsParentPayments.init(),
		subChangeState: null,
		lastFinishedSCS: null,
		isCancelPending: false,
		hasPassword: true, // default to true so the page shows the password change link by default
		deferrals: null, // null = not yet loaded; [] = loaded but none pending
	});
};

const loadStart = function(parentAcc, api) {
	return LoadState.loadStart(parentAcc, () => api.loadAccount());
};

// Helper for loadSuccess. Selects one subscription per student ID from the
// given array, using the latest createdAt if there are multiple. Needed to
// correctly implement reactivation.
const pickCurrentSubscriptions = function(subscriptions) {
	const byStudentID = {};
	subscriptions.forEach(s => {
		const studentID = s.student.id;
		if (!byStudentID[studentID]) {
			byStudentID[studentID] = s;
		} else if (byStudentID[studentID].createdAt < s.createdAt) {
			byStudentID[studentID] = s;
		}
	});
	return _.values(byStudentID);
};

const loadSuccess = function(parentAcc, rawData, now) {
	parentAcc = LoadState.loadSuccess(parentAcc);
	if (!rawData.owner) {
		return setIn(parentAcc, "isOwner", false);
	}

	const rawO = rawData.owner;
	const shownCC = rawO.payment.token ? rawO.creditCard : null;
	const hasShipping = rawO.hasShipping;
	const payments = OpsParentPayments.setCredit(parentAcc.payments, rawO.credit);

	let storedForm = OpsCheckoutForm.init("parent", now);
	if (hasShipping) {
		storedForm = OpsCheckoutForm.setData(storedForm, "shipping", rawO.shipping);
	}
	storedForm = OpsCheckoutForm.setData(storedForm, "billing", rawO.billing);
	storedForm = OpsCheckoutForm.setData(storedForm, "contact", rawO.contact);
	storedForm = OpsCheckoutForm.resetSection(storedForm, "creditCard", now);

	const platformStudents = rawO.platformStudents;

	// TODO: We added new platformStudents rather than combine them into students. This has caused some complexity.
	// Going to leave them separate for now, but since this is named "students" and not "actively subscribed students",
	// its worth considering combining them.
	const students = pickCurrentSubscriptions(rawData.subscriptions).map(s => {
		let refundedAt = null;
		const latestAction = s.history[0];
		if (latestAction && latestAction.type === "refund") {
			refundedAt = latestAction.createdAt;
		}
		const paymentsHistory = _.filter(
			s.history,
			h => h.type === "payment" || h.type === "upgrade"
		);
		const booksCanceled = _.some(s.history, h => h.type === "cancel-books");
		const subPlanType = s.isBundle ? "bundle" : s.planType;
		return {
			userID: s.student.id,
			platformUserID: s.student.platformUserID,
			avatar: s.student.avatarData,
			username: s.student.username,
			displayName: s.student.displayName,
			firstName: s.student.firstName,
			lastName: s.student.lastName,
			grade: s.student.grade,
			customerSegment: s.student.customerSegment,
			lockSettings: s.student.lockSettings,
			hasCode: s.student.hasCode,
			usedCode: s.student.usedCode,
			beastLock: s.student.beastLock,
			leaderboardsLock: s.student.leaderboardsLock,
			inActiveClasses: s.student.inActiveClasses,
			latestAcademyClassEndTime: s.student.latestAcademyClassEndTime,
			subscription: {
				subscriptionID: s.id,
				active: s.active,
				planType: subPlanType,
				effectivePlanType: s.effectivePlanType || subPlanType,
				expiresAt: s.expiresAt,
				expiredAt: s.expiredAt,
				weeklyNotification: s.weeklyNotification,
				autoRenew: s.autoRenew,
				refundable: s.refundable,
				completeRefund: s.completeRefund,
				booksOnlyRefund: s.booksOnlyRefund,
				onlineOnlyRefund: s.onlineOnlyRefund,
				canceledAt: s.canceledAt,
				booksCanceled,
				refundedAt,
				lastRenewedAt: s.lastRenewedAt,
				purchaseOrderID: s.purchaseOrderID,
				failedAttempts: s.failedAttempts,
				usedB2BBulkCode: !!s.usedB2BBulkCode,
				usedB2CBulkCode: !!s.usedB2CBulkCode,
				isTrial: !!s.trial,
				payments: paymentsHistory,
				hasSwitched: !!(
					_.isArray(paymentsHistory) &&
					paymentsHistory.length > 0 &&
					_.find(
						s.history,
						h =>
							h.type === "switch-type" &&
							moment(h.createdAt).isAfter(paymentsHistory[0].createdAt)
					)
				),
			},
			additionalEmails: s.student.additionalEmails,
		};
	});

	parentAcc = extendIn(parentAcc, [], {
		isOwner: true,
		shownCC,
		hasShipping,
		hasPassword: rawO.hasPassword,
		storedForm,
		students,
		platformStudents,
		payments,
	});
	return parentAcc;
};

// Call when we might be out of sync with the server, which can happen due to
// weird failures with saving data or when a complex thing like changing a
// subscription occurs.
const markForReload = function(parentAcc) {
	// Leaves all data in place but doesn't let view show it until reload.
	parentAcc = setIn(parentAcc, ["payments"], OpsParentPayments.init());
	return LoadState.loadRestart(parentAcc);
};

// Gets array of displayNames for students. Used for validation purposes.
const getDisplayNames = function(parentAcc) {
	return parentAcc.students.map(s => s.displayName);
};

// Optional second parameter is a potential new name to push.
const getBadDisplayName = function(parentAcc, newName) {
	let names = getDisplayNames(parentAcc);
	// Put newName in front so that it's more likely to appear in an error.
	if (newName) names = [newName].concat(names);
	return StoreUtil.getBadNameInSubscriptions(names);
};

// Returns false if parentAcc has too many subscriptions to add one more.
const canAddStudent = function(parentAcc) {
	if (!parentAcc.shownCC) return false;
	const allStudents = parentAcc.students;
	const activeStudents = allStudents.filter(s => s.subscription.active);
	return (
		allStudents.length < MAX_INACTIVE_SUBSCRIPTIONS_FOR_PARENT &&
		activeStudents.length < MAX_SUBSCRIPTIONS_FOR_PARENT
	);
};

// Returns false if parentAcc has too many subscriptions to activate one more.
const canActivateStudent = function(parentAcc) {
	if (!parentAcc.shownCC) return false;
	const allStudents = parentAcc.students;
	const activeStudents = allStudents.filter(s => s.subscription.active);
	return activeStudents.length < MAX_SUBSCRIPTIONS_FOR_PARENT;
};

// Generic, internal handler for a save succeeding. If specialized behavior
// needed, write another function that starts by calling this one.
const saveSuccess = function(parentAcc, emitEvent) {
	parentAcc = OpsEditSave.onSuccess(parentAcc, emitEvent, "parentAcc");
	return parentAcc;
};

// Generic, internal handler for a save failing.
const saveFail = function(parentAcc, emitEvent, error, moreInfo) {
	parentAcc = OpsEditSave.onFail(
		parentAcc,
		emitEvent,
		"parentAcc",
		error,
		moreInfo
	);
	if (OpsEditSave.shouldReload(parentAcc, "parentAcc")) {
		parentAcc = markForReload(parentAcc);
	}
	return parentAcc;
};

const getEmail = function(parentAcc) {
	const contact = OpsCheckoutForm.getSection(parentAcc.storedForm, "contact");
	return contact.email;
};

const shouldBlockPage = function(parentAcc) {
	return parentAcc.isCancelPending;
};

const hasPaymentMethod = function(parentAcc) {
	return !!parentAcc.shownCC;
};

const editFormData = function(parentAcc, emitEvent, api, section, values) {
	let newForm = OpsCheckoutForm.updateData(
		parentAcc.storedForm,
		section,
		values
	).form;
	newForm = OpsCheckoutForm.validateSection(newForm, section, {
		addMissing: true,
		// Skip validating phone number if they are only updating their e-mail address.
		// PO accounts start with e-mail addresses but no phone numbers.
		skipFields: {
			phone:
				_.has(values, "email") &&
				!_.has(values, "phone") &&
				!_.get(parentAcc, "storedForm.data.contact.phone"),
		},
	});
	if (OpsCheckoutForm.isSectionComplete(newForm, section)) {
		parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
			eventName: "parentForm",
			doSave: () => saveFormStart(parentAcc, api, newForm),
			dataToSet: [["storedForm", newForm]],
		});
	} else {
		emitEvent(
			"parentFormValidationFail",
			OpsCheckoutForm.checkSection(newForm, section)
		);
	}
	return parentAcc;
};

const getFormSection = function(parentAcc, section) {
	return OpsCheckoutForm.getSection(parentAcc.storedForm, section);
};

// Not exported, only called internally.
const saveFormStart = function(parentAcc, api, newForm) {
	const payload = {};
	OpsCheckoutForm.getSectionNames(newForm).forEach(n => {
		const newData = OpsCheckoutForm.getSection(newForm, n);
		const oldData = OpsCheckoutForm.getSection(parentAcc.storedForm, n);
		if (newData !== oldData && OpsCheckoutForm.isSectionComplete(newForm, n)) {
			payload[n] = newData;
		}
	});
	api.updateOwner(payload);
	return parentAcc;
};

const saveFormSuccess = function(parentAcc, emitEvent, response, payload, now) {
	parentAcc = saveSuccess(parentAcc, emitEvent);
	if (payload.creditCard) {
		parentAcc = setIn(parentAcc, ["shownCC"], {
			cardType: StoreUtil.getCardType(payload.creditCard.number),
			cc4: payload.creditCard.number.substr(-4, 4),
			expYear: payload.creditCard.expYear,
			expMonth: payload.creditCard.expMonth,
		});
		parentAcc = setIn(parentAcc, ["storedForm"], f =>
			OpsCheckoutForm.resetSection(f, "creditCard", now)
		);
	} else if (payload.billing) {
		parentAcc = setIn(parentAcc, ["storedForm"], f => {
			return OpsCheckoutForm.setVerifiedAddress(
				f,
				"billing",
				response.verifiedBilling
			);
		});
	} else if (payload.shipping) {
		parentAcc = setIn(parentAcc, ["hasShipping"], true);
		parentAcc = setIn(parentAcc, ["storedForm"], f => {
			return OpsCheckoutForm.setVerifiedAddress(
				f,
				"shipping",
				response.verifiedShipping
			);
		});
	}
	return parentAcc;
};

const saveFormFail = saveFail;

const deleteCreditCard = function(parentAcc, emitEvent, api, now) {
	const newForm = OpsCheckoutForm.resetSection(
		parentAcc.storedForm,
		"creditCard",
		now
	);
	parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
		eventName: "deleteCreditCard",
		doSave: () =>
			api.removeOwner({
				creditCard: OpsCheckoutForm.getSection(newForm, "creditCard"),
				skipChecks: true,
			}),
		dataToSet: [
			["storedForm", newForm],
			["shownCC", null],
		],
	});
	return parentAcc;
};

const deleteCreditCardSuccess = function(parentAcc, emitEvent) {
	parentAcc = OpsEditSave.onSuccess(parentAcc, emitEvent, "parentAcc");
	// Backend made a lot of updates, e.g. autorenew false, so reload.
	parentAcc = markForReload(parentAcc);
	return parentAcc;
};

const deleteCreditCardFail = saveFail;

const getStudentCursor = function(parentAcc, studentID) {
	const index = _.findIndex(parentAcc.students, s => s.userID === studentID);
	if (index === -1) return Cursor(parentAcc).bad();
	return Cursor(parentAcc, ["students", index]);
};

const getPlatformStudent = function(parentAcc, studentID) {
	return _.find(parentAcc.platformStudents, s => s.userID === studentID);
};

const getStudentDisplayName = function(parentAcc, studentID, parentData) {
	if (!studentID) {
		return null;
	}
	const studentCR = getStudentCursor(parentAcc, studentID);
	if (studentCR.exists()) {
		return studentCR.getIn("displayName");
	}
	const parentStudent = parentData?.students?.[studentID];
	return parentStudent?.displayName ?? null;
};

const isStudentInActiveClasses = function(parentAcc, studentID, parentData) {
	if (!studentID) {
		return false;
	}
	const studentCR = getStudentCursor(parentAcc, studentID);
	if (studentCR.exists()) {
		return !!studentCR.getIn("inActiveClasses");
	}
	const parentStudent = parentData?.students?.[studentID];
	return !!(parentStudent?.inActiveClasses ?? false);
};

const getStudentAccessEndsAt = function(parentAcc, studentID) {
	if (!studentID) {
		return null;
	}
	const studentCR = getStudentCursor(parentAcc, studentID);
	if (!studentCR.exists()) {
		return null;
	}
	const latestAcademyClassEndTime = studentCR.getIn(
		"latestAcademyClassEndTime"
	);
	const expiresAt = studentCR.getIn(["subscription", "expiresAt"]);
	return latestAcademyClassEndTime ?? expiresAt;
};

const editStudentInfo = function(parentAcc, emitEvent, api, studentID, data) {
	const studentInfoKeys = ["displayName", "grade", "lockSettings"];
	data = _.pick(data, studentInfoKeys);
	if (!data || _.isEmpty(data)) return parentAcc;

	const studentCR = getStudentCursor(parentAcc, studentID);
	if (!studentCR.exists()) return parentAcc;

	if (data.displayName === "") {
		emitEvent("parentInvalidDisplayName", "", "E_MISSING");
		return parentAcc;
	} else if (data.displayName) {
		const newParent = studentCR.setIn("displayName", data.displayName);
		const bad = getBadDisplayName(newParent);
		if (bad && bad.name === data.displayName) {
			emitEvent("parentInvalidDisplayName", bad.name, bad.error);
			return parentAcc;
		}
	}

	const dataToSet = _.toPairs(data).map(r => [
		studentCR.getPath().concat([r[0]]),
		r[1],
	]);
	parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
		eventName: "parentStudentInfo",
		doSave: () =>
			api.updateStudent(studentID, studentCR.getIn("platformUserID"), data),
		dataToSet,
	});
	return parentAcc;
};

const saveStudentInfoSuccess = saveSuccess;

const saveStudentInfoFail = function(parentAcc, emitEvent, error, moreInfo) {
	if (error === "E_PERMISSION" || error === "E_DISPLAYNAME_EXISTS") {
		parentAcc = markForReload(parentAcc);
	}
	return saveFail(parentAcc, emitEvent, error, moreInfo);
};

const editParentName = function(parentAcc, emitEvent, api, nameData) {
	const sanitized = _.pick(nameData, ["firstName", "lastName"]);
	if (_.isEmpty(sanitized)) return parentAcc;

	const errorTypes = {};
	if (sanitized.firstName !== undefined && !sanitized.firstName.trim()) {
		errorTypes.firstName = {type: "E_MISSING"};
	}
	if (sanitized.lastName !== undefined && !sanitized.lastName.trim()) {
		errorTypes.lastName = {type: "E_MISSING"};
	}
	if (!_.isEmpty(errorTypes)) {
		emitEvent("parentFormValidationFail", {errorTypes});
		return parentAcc;
	}

	parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
		eventName: "parentForm",
		doSave: () => {
			api.updateParentName(sanitized.firstName, sanitized.lastName);
			return parentAcc;
		},
		dataToSet: [],
	});
	return parentAcc;
};

const saveParentNameSuccess = saveSuccess;

const saveParentNameFail = saveFail;

const changeParentPassword = function(
	parentAcc,
	emitEvent,
	api,
	originalPassword,
	password,
	passwordRepeat
) {
	const checkResult = checkEmailPassword(password, passwordRepeat);
	if (checkResult.valid) {
		parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
			eventName: "parentPassword",
			doSave: () => api.changeParentPassword(originalPassword, password),
			dataToSet: [],
		});
	} else {
		emitEvent("parentPasswordInvalid", checkResult.error);
	}
	return parentAcc;
};

const saveParentPasswordSuccess = saveSuccess;

const saveParentPasswordFail = (parentAcc, emitEvent, error, moreInfo) => {
	if (error === "E_INVALID_PASSWORD") {
		emitEvent("parentPasswordInvalid", moreInfo);
		return setIn(parentAcc, ["es", "parentAcc"], null);
	} else {
		return saveFail(parentAcc, emitEvent, error, moreInfo);
	}
};

const changeStudentPassword = function(
	parentAcc,
	emitEvent,
	api,
	studentID,
	password,
	passwordRepeat
) {
	const studentCR = getStudentCursor(parentAcc, studentID);
	if (!studentCR.exists()) return parentAcc;
	const checkResult = checkPassword(password, passwordRepeat, studentCR.get());

	if (checkResult.valid) {
		parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
			eventName: "parentStudentPassword",
			doSave: () => api.changeStudentPassword(studentID, password),
			dataToSet: [],
		});
	} else {
		emitEvent("parentStudentPasswordInvalid", checkResult.error);
	}
	return parentAcc;
};

const saveStudentPasswordSuccess = saveSuccess;

const saveStudentPasswordFail = saveFail;

const editSubscriptionInfo = function(
	parentAcc,
	emitEvent,
	api,
	studentID,
	data
) {
	const subInfoKeys = ["weeklyNotification", "autoRenew"];
	data = _.pick(data, subInfoKeys);
	if (!data || _.isEmpty(data)) return parentAcc;

	const studentCR = getStudentCursor(parentAcc, studentID);
	if (!studentCR.exists()) return parentAcc;
	const subscriptionID = studentCR.getIn(["subscription", "subscriptionID"]);
	const dataToSet = _.toPairs(data).map(r => {
		const keyPath = studentCR.getPath().concat(["subscription", r[0]]);
		return [keyPath, r[1]];
	});

	parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
		eventName: "parentSubscriptionInfo",
		doSave: () => api.editSubscription(subscriptionID, data),
		dataToSet,
		optimistic: true,
	});
	return parentAcc;
};

const saveSubscriptionInfoSuccess = saveSuccess;

const saveSubscriptionInfoFail = function(
	parentAcc,
	emitEvent,
	error,
	moreInfo
) {
	if (error === "E_INVALID_SUBSCRIPTION") {
		parentAcc = markForReload(parentAcc);
	}
	return saveFail(parentAcc, emitEvent, error, moreInfo);
};

const editBeastbuilderAccess = function(
	parentAcc,
	emitEvent,
	api,
	studentID,
	lockStatus
) {
	const studentCR = getStudentCursor(parentAcc, studentID);
	if (!studentCR.exists()) return parentAcc;
	const keyPath = studentCR.getPath().concat(["beastLock"]);
	const dataToSet = [[keyPath, lockStatus]];

	parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
		eventName: "parentSubscriptionInfo",
		doSave: () =>
			api.setSpaceLockOverride("beastbuilder", lockStatus, studentID),
		dataToSet,
		optimistic: true,
	});
	return parentAcc;
};

const editLeaderboardsAccess = function(
	parentAcc,
	emitEvent,
	api,
	studentID,
	lockStatus
) {
	const studentCR = getStudentCursor(parentAcc, studentID);
	if (!studentCR.exists()) return parentAcc;
	const keyPath = studentCR.getPath().concat(["leaderboardsLock"]);
	const dataToSet = [[keyPath, lockStatus]];

	parentAcc = OpsEditSave.doRequest(parentAcc, emitEvent, "parentAcc", {
		eventName: "parentSubscriptionInfo",
		doSave: () =>
			api.setSpaceLockOverride("leaderboards", lockStatus, studentID),
		dataToSet,
		optimistic: true,
	});
	return parentAcc;
};

const sendCancelRequest = function(
	parentAcc,
	api,
	studentID,
	refundBooks,
	refundOnline
) {
	if (parentAcc.isCancelPending) return parentAcc;
	const studentCR = getStudentCursor(parentAcc, studentID);
	if (!studentCR.exists()) return parentAcc;
	const subscriptionID = studentCR.getIn(["subscription", "subscriptionID"]);

	parentAcc = setIn(parentAcc, ["isCancelPending"], true);
	api.subCancel(studentID, subscriptionID, refundBooks, refundOnline);
	return parentAcc;
};

const onCancelSuccess = function(
	parentAcc,
	emitEvent,
	studentID,
	refundBooks,
	refundOnline
) {
	const studentCR = getStudentCursor(parentAcc, studentID);
	const displayName = studentCR.getIn("displayName");
	const subscriptionID = studentCR.getIn(["subscription", "subscriptionID"]);
	const planType = studentCR.getIn(["subscription", "planType"]);
	const usedB2BBulkCode = studentCR.getIn(["subscription", "usedB2BBulkCode"]);
	parentAcc = setIn(parentAcc, ["isCancelPending"], false);
	parentAcc = markForReload(parentAcc);
	emitEvent(
		"cancelSubscriptionSuccess",
		displayName,
		subscriptionID,
		planType,
		usedB2BBulkCode,
		refundBooks,
		refundOnline
	);
	return parentAcc;
};

const onCancelFail = function(parentAcc, emitEvent, error, studentID) {
	parentAcc = setIn(parentAcc, ["isCancelPending"], false);
	let shouldLeave = false;

	if (error === "E_TOO_MANY_REQUESTS") {
		shouldLeave = true;
	} else if (
		error === "E_NO_OWNER" ||
		error === "E_INVALID_SUBSCRIPTION" ||
		error === "E_UNABLE_TO_CANCEL"
	) {
		// These might be okay if there is some kind of synchronization error
		// between client and server, but it might also indicate a serious issue.
		ErrorUtil.log(
			"E_SUBSCRIPTION_CANCEL_INVALID",
			`User got ${error} while trying to cancel student ${studentID}`
		);
		shouldLeave = true;
		parentAcc = markForReload(parentAcc);
		emitEvent("cancelSubscriptionInvalid");
	} else if (error === "E_CHECKOUT_CARD_DECLINED") {
		emitEvent("cardDeclined");
	} else if (error === "E_CHECKOUT_SERVER_TIMEOUT") {
		emitEvent("checkoutServerTimeout");
	} else if (error === "E_MULTIPLE_FAILED_CHECKOUTS") {
		emitEvent("multipleFailedCheckouts");
	} else if (
		error === "E_CHECKOUT_INPUT" ||
		error === "E_CHECKOUT_DUPLICATE" ||
		error === "E_SUSPECTED_FRAUD" ||
		error === "E_CHECKOUT_UNKNOWN"
	) {
		emitEvent("cancelSubscriptionFail");
	} else {
		// Timeout or unrecognized error code.
		shouldLeave = true;
		parentAcc = markForReload(parentAcc);
		emitEvent("cancelSubscriptionUnknown", studentID);
	}
	return {parentAcc, shouldLeave};
};

const cancelOtherSubscriptionFirst = function(parentAcc, emitEvent, studentID) {
	const allStudents = parentAcc.students;
	const otherActiveYearlyStudents = allStudents.filter(
		s =>
			s.subscription.active &&
			s.subscription.planType === "yearly" &&
			s.userID !== studentID
	);
	parentAcc = setIn(parentAcc, ["isCancelPending"], false);
	emitEvent("cancelOtherSubscription", otherActiveYearlyStudents);
	return parentAcc;
};

const sendCancelReasonRequest = function(
	parentAcc,
	api,
	subscriptionID,
	reason
) {
	api.subCancelReason(subscriptionID, reason);
	return parentAcc;
};

const onCancelReasonSuccess = function(parentAcc, emitEvent) {
	emitEvent("cancelReasonSuccess");
	return parentAcc;
};

const deferralsLoaded = function(parentAcc, deferrals) {
	return setIn(parentAcc, ["deferrals"], deferrals);
};

const deferralResolved = function(parentAcc, deferralID) {
	const deferrals = parentAcc.deferrals || [];
	return setIn(
		parentAcc,
		["deferrals"],
		deferrals.filter(d => d.id !== deferralID)
	);
};

module.exports = {
	init,
	loadStart,
	loadSuccess,
	loadError: LoadState.loadError,
	markForReload,
	getDisplayNames,
	getBadDisplayName,
	canAddStudent,
	canActivateStudent,
	getFormSection,
	getEmail,
	shouldBlockPage,
	hasPaymentMethod,
	editFormData,
	saveFormSuccess,
	saveFormFail,
	editParentName,
	saveParentNameSuccess,
	saveParentNameFail,
	deleteCreditCard,
	deleteCreditCardSuccess,
	deleteCreditCardFail,
	getStudentCursor,
	editStudentInfo,
	saveStudentInfoSuccess,
	saveStudentInfoFail,
	changeParentPassword,
	saveParentPasswordSuccess,
	saveParentPasswordFail,
	changeStudentPassword,
	saveStudentPasswordSuccess,
	saveStudentPasswordFail,
	editSubscriptionInfo,
	saveSubscriptionInfoSuccess,
	saveSubscriptionInfoFail,
	editBeastbuilderAccess,
	editLeaderboardsAccess,
	sendCancelRequest,
	onCancelSuccess,
	onCancelFail,
	cancelOtherSubscriptionFirst,
	sendCancelReasonRequest,
	onCancelReasonSuccess,
	getPlatformStudent,
	isStudentInActiveClasses,
	getStudentAccessEndsAt,
	getStudentDisplayName,
	deferralsLoaded,
	deferralResolved,
};
